import { useEffect, useRef } from 'react';
import { isMobile, isMobileWidth, MOBILE_SAFE_RESIZE, snapTargetClass, } from './globalUtils';
import usePageStore from './Page/pageStore';
import useDataStore from './Data/dataStore';
export const FONT_SIZE = 16;
export const MAX_WINDOW = 1460;
export const MIN_WINDOW = 320;
export const MAX_HEIGHT = 880;
const baseSideGap = 100;
const minFontSize = 8;
export const map = (value, min, max) => {
    return min + value * (max - min);
};
const mapMin = (value, min, max) => {
    return Math.max(minFontSize, map(value, min, max));
};
export const CARD_WIDTH_CSS_VAR = '--cardWidthNum';
export const MOBILE_KEYBOARD_CLASS = 'mobile-keyboard';
export const mobileCardWidth = 200;
const Resizer = () => {
    const { activeSection, shownPage } = usePageStore();
    const snap = useDataStore((state) => state.data[shownPage]?.snap);
    const activeSectionRef = useRef(activeSection);
    activeSectionRef.current = snap ? activeSection : -1;
    const mounted = useRef(false);
    const dimensions = useRef({ width: 0, height: 0 });
    const resizeDimensions = useRef({ width: 0, height: 0 });
    useEffect(() => {
        const handleFocusIn = (e) => {
            const target = e.target;
            if ((target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') &&
                isMobile) {
                if (!document.body.classList.contains(MOBILE_KEYBOARD_CLASS)) {
                    document.body.classList.add(MOBILE_KEYBOARD_CLASS);
                }
            }
        };
        const handleFocusOut = (e) => {
            if (document.body.classList.contains(MOBILE_KEYBOARD_CLASS)) {
                document.body.classList.remove(MOBILE_KEYBOARD_CLASS);
            }
        };
        document.addEventListener('focusin', handleFocusIn);
        document.addEventListener('focusout', handleFocusOut);
        if (isMobile) {
            document.body.classList.add('mobile-browser');
        }
        return () => {
            document.removeEventListener('focusin', handleFocusIn);
            document.removeEventListener('focusout', handleFocusOut);
        };
    }, []);
    useEffect(() => {
        if (mounted.current)
            return;
        mounted.current = true;
        window.addEventListener('resize', onResize);
        onResize();
    }, []);
    const onResize = () => {
        const blockResize = isMobile &&
            resizeDimensions.current.height === window.outerHeight &&
            resizeDimensions.current.width === window.innerWidth;
        resizeDimensions.current = {
            width: window.innerWidth,
            height: window.outerHeight,
        };
        if (blockResize)
            return;
        window.dispatchEvent(new CustomEvent(MOBILE_SAFE_RESIZE));
        dimensions.current = {
            width: window.innerWidth,
            height: window.innerHeight,
        };
        if (mounted.current) {
            const snapElement = document.querySelector(`.${snapTargetClass}${activeSectionRef.current}`);
            if (snapElement) {
                setTimeout(() => {
                    const style = window.getComputedStyle(snapElement);
                    const topNum = parseFloat(style.top) || 0;
                    window.scrollTo({
                        behavior: 'instant',
                        top: topNum,
                    });
                }, 10);
            }
        }
        requestAnimationFrame(applyResize);
    };
    const applyResize = () => {
        const { width, height } = dimensions.current;
        let fontScale = Math.min(1, (width - MIN_WINDOW) / (MAX_WINDOW - MIN_WINDOW), height / MAX_HEIGHT);
        const sideGap = width < MAX_WINDOW
            ? map(fontScale, 16, baseSideGap)
            : (width - MAX_WINDOW + 2 * baseSideGap) / 2;
        if (height < 460) {
            fontScale = 0;
        }
        const cardWidth = map(fontScale, 136, 376);
        const textFontSize = mapMin(fontScale, 8, 18);
        const mobileCardWidth = textFontSize * 26;
        const scaleSetup = {
            ['--rootFontSize']: `${FONT_SIZE}px`,
            ['--scaledFontSize']: `${(FONT_SIZE * width) / MAX_WINDOW}px`, // TODO: remove
            ['--fontScale']: `${fontScale}`,
            ['--headerScale']: `${map(fontScale, 0.4, 1)}`,
            ['--textFontSize']: `${textFontSize}px`,
            ['--textLargeFontSize']: `${mapMin(fontScale, 8, 24)}px`,
            ['--titleFontSize']: `${mapMin(fontScale, 12, 28)}px`,
            ['--titleLargeFontSize']: `${mapMin(fontScale, 24, 60)}px`,
            ['--headerFontSize']: `${mapMin(fontScale, 24, 80)}px`,
            ['--menuFontSize']: `${mapMin(fontScale, 14, 16)}px`,
            ['--sideGap']: `${sideGap}px`,
            ['--pageWidth']: `${width - 2 * sideGap}px`,
            ['--pageWidthNumeric']: `${width - 2 * sideGap}`,
            ['--mobileDisplay']: height <= MAX_HEIGHT ? 'none' : 'block',
            ['--mobileDisplayFlex']: height <= MAX_HEIGHT ? 'none' : 'flex',
            ['--cardWidth']: `${cardWidth}px`,
            [CARD_WIDTH_CSS_VAR]: `${isMobileWidth() ? mobileCardWidth : cardWidth}`,
        };
        Object.keys(scaleSetup).forEach((key) => document.body.style.setProperty(key, scaleSetup[key]));
        /* const vh = window.visualViewport?.height ?? window.innerHeight;
        const total = window.innerHeight;
    
         if (vh < total - 100) {
          if (!document.body.classList.contains(MOBILE_KEYBOARD_CLASS)) {
            document.body.classList.add(MOBILE_KEYBOARD_CLASS);
          }
        } else {
          if (document.body.classList.contains(MOBILE_KEYBOARD_CLASS)) {
            document.body.classList.remove(MOBILE_KEYBOARD_CLASS);
          }
        } */
    };
    if (!mounted.current) {
        onResize();
    }
    return null;
};
export default Resizer;
